import { useCallback, useMemo, useState } from "react";
import type { z } from "zod";
import {
  type ArrayFieldErrors,
  type ZodIssueLike,
  mapZodIssuesToFieldErrors,
} from "@/api/profiles/validationErrors";
import type { ValidationSummaryEntry } from "./FormValidationSummary/FormValidationSummary";

/**
 * Map of human-readable labels keyed by the leaf field key used in the Zod
 * schema. Pass labels that exactly match the visible <label> on each input —
 * the FormValidationSummary surfaces them verbatim to screen-reader users.
 */
export type FieldLabelMap<TKey extends string> = Record<TKey, string>;

type FieldErrors<TKey extends string> = Partial<Record<TKey, string>>;

/**
 * Friendly labels used when building summary entries for nested array-field
 * errors, keyed by the array field name (e.g. `"staffing"`).
 */
export type ArrayFieldLabel = {
  /** Section heading shown in the summary, e.g. "Staffing". */
  section: string;
  /** Singular noun for a row; defaults to "row". */
  rowNoun?: string;
  /** Sub-field labels keyed by sub-field name, e.g. `{ title: "Title" }`. */
  subFields?: Record<string, string>;
};

export type UseFormValidationResult<TKey extends string, TSchema extends z.ZodType> = {
  /** Per-field error messages, keyed by Zod path head (or full dotted path). */
  errors: FieldErrors<TKey>;
  /**
   * Per-row errors for repeatable array fields. Access as
   * `arrayErrors.staffing?.[0]?.title`. Sparse; row-level/primitive errors live
   * under the empty-string sub-key (`arrayErrors.field?.[i]?.[""]`).
   */
  arrayErrors: ArrayFieldErrors;
  /** Ordered list of `{fieldKey, fieldLabel, message}` for the top-of-page summary. */
  summary: ValidationSummaryEntry[];
  /** Orphan messages (paths that don't map to a known field). */
  orphanMessages: string[];
  /** Has the user attempted a submit at least once? Drives onChange revalidation. */
  hasSubmitted: boolean;
  /**
   * Run the Zod schema against `input` and update state. Returns the parsed
   * data on success (typed via the schema) or `null` on failure.
   */
  validate: (input: z.input<TSchema>) => z.output<TSchema> | null;
  /** Re-validate the supplied input *only if* `hasSubmitted` is true. */
  revalidate: (input: z.input<TSchema>) => void;
  /** Clear the error for a single field — call from onChange handlers. */
  clearError: (key: TKey) => void;
  /**
   * Clear a single nested array-field error (and its summary entry) — call from
   * a Repeater row's onChange. Pass `sub` for a sub-field error, or omit it
   * (or pass `""`) for a row-level/primitive error.
   */
  clearArrayError: (field: string, index: number, sub?: string) => void;
  /** Drop every error and reset `hasSubmitted` (e.g. after a successful save). */
  reset: () => void;
  /**
   * Merge server-side Zod issues (from `extractZodIssues`) into the same
   * error state so server and client errors render identically. Sets
   * `hasSubmitted` to true so the summary renders.
   */
  setServerIssues: (issues: ReadonlyArray<ZodIssueLike>) => void;
  /**
   * Convenience: the first invalid field key, useful for focus management.
   * Reflects the order of `summary`.
   */
  firstInvalidKey: TKey | null;
};

type UseFormValidationOptions<TSchema extends z.ZodType, TKey extends string> = {
  schema: TSchema;
  /** Human-readable labels for every key in the schema. */
  labels: FieldLabelMap<TKey>;
  /**
   * Stable list of known field keys. Used to map both client and server Zod
   * issues to a field. Should match the keys in `labels`.
   */
  knownKeys: ReadonlyArray<TKey>;
  /**
   * Names of repeatable array fields (e.g. `["staffing", "memberships"]`).
   * Issues on `field.<index>.<sub>` paths are routed into `arrayErrors`.
   */
  arrayKeys?: ReadonlyArray<string>;
  /** Friendly labels for building summary entries from nested array errors. */
  arrayFieldLabels?: Record<string, ArrayFieldLabel>;
};

/**
 * Append summary entries for one array field's per-row errors, in index order
 * then sub-field (insertion) order. Labels fall back to raw field/sub names.
 */
function appendArraySummary(
  out: ValidationSummaryEntry[],
  field: string,
  rows: Array<Record<string, string> | undefined> | undefined,
  label: ArrayFieldLabel | undefined,
): void {
  if (!rows) return;
  const section = label?.section ?? field;
  const rowNoun = label?.rowNoun ?? "row";
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    if (!row) continue;
    for (const sub of Object.keys(row)) {
      const message = row[sub];
      if (!message) continue;
      const fieldKey = sub ? `${field}.${index}.${sub}` : `${field}.${index}`;
      const subLabel = sub ? (label?.subFields?.[sub] ?? sub) : "";
      const fieldLabel = `${section} ${rowNoun} ${index + 1}${subLabel ? ` — ${subLabel}` : ""}`;
      out.push({ fieldKey, fieldLabel, message });
    }
  }
}

/**
 * Shared validation state machine for staff-ui profile forms. Ported from the
 * canonical account-ui hook so both portals validate identically.
 *
 * Lifecycle:
 *  1. The form calls `validate(input)` on submit; this records the user has
 *     submitted, runs Zod, and stores any errors.
 *  2. While `hasSubmitted` is true, the form should call `clearError(key)`
 *     from each field's onChange so the inline error vanishes as soon as the
 *     user starts fixing it.
 *  3. On success the caller calls `reset()` after navigation.
 *
 * Server errors flow through `setServerIssues`, reusing the same path-mapping
 * helper so the abstraction handles both error sources uniformly.
 */
export function useFormValidation<TSchema extends z.ZodType, TKey extends string>(
  options: UseFormValidationOptions<TSchema, TKey>,
): UseFormValidationResult<TKey, TSchema> {
  const { schema, labels, knownKeys, arrayKeys, arrayFieldLabels } = options;

  const [errors, setErrors] = useState<FieldErrors<TKey>>({});
  const [arrayErrors, setArrayErrors] = useState<ArrayFieldErrors>({});
  const [orphanMessages, setOrphanMessages] = useState<string[]>([]);
  const [orderedKeys, setOrderedKeys] = useState<TKey[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const buildSummary = useCallback(
    (
      errs: FieldErrors<TKey>,
      order: TKey[],
      arrErrs: ArrayFieldErrors,
    ): ValidationSummaryEntry[] => {
      const seen = new Set<TKey>();
      const out: ValidationSummaryEntry[] = [];
      // Flat errors first, in the order Zod reported them.
      for (const key of order) {
        const message = errs[key];
        if (!message || seen.has(key)) continue;
        seen.add(key);
        out.push({
          fieldKey: key,
          fieldLabel: labels[key] ?? key,
          message,
        });
      }
      // Then array errors, grouped by field in arrayKeys order, then by index,
      // then by sub-field (insertion order within the row map).
      const seenArrayKeys = new Set<string>();
      for (const field of arrayKeys ?? []) {
        seenArrayKeys.add(field);
        appendArraySummary(out, field, arrErrs[field], arrayFieldLabels?.[field]);
      }
      // Defensive: surface array errors for any field not declared in arrayKeys
      // so nothing is lost.
      for (const field of Object.keys(arrErrs)) {
        if (seenArrayKeys.has(field)) continue;
        appendArraySummary(out, field, arrErrs[field], arrayFieldLabels?.[field]);
      }
      return out;
    },
    [labels, arrayKeys, arrayFieldLabels],
  );

  const summary = useMemo(
    () => buildSummary(errors, orderedKeys, arrayErrors),
    [errors, orderedKeys, arrayErrors, buildSummary],
  );

  const runZod = useCallback(
    (input: z.input<TSchema>) => {
      const parsed = schema.safeParse(input);
      if (parsed.success) {
        setErrors({});
        setArrayErrors({});
        setOrderedKeys([]);
        setOrphanMessages([]);
        return parsed.data as z.output<TSchema>;
      }
      const issues: ZodIssueLike[] = parsed.error.issues.map(issue => ({
        path: issue.path as ReadonlyArray<string | number>,
        message: issue.message,
        code: issue.code,
      }));
      const {
        fieldErrors,
        arrayErrors: arrErrs,
        orphanMessages: orphans,
      } = mapZodIssuesToFieldErrors(issues, knownKeys, arrayKeys);
      const order: TKey[] = [];
      for (const issue of issues) {
        const head = issue.path.length > 0 ? String(issue.path[0]) : "";
        const full = issue.path.map(String).join(".");
        const matched =
          full && (knownKeys as ReadonlyArray<string>).includes(full)
            ? (full as TKey)
            : head && (knownKeys as ReadonlyArray<string>).includes(head)
              ? (head as TKey)
              : null;
        if (matched && !order.includes(matched)) order.push(matched);
      }
      setErrors(fieldErrors);
      setArrayErrors(arrErrs);
      setOrderedKeys(order);
      setOrphanMessages(orphans);
      return null;
    },
    [schema, knownKeys, arrayKeys],
  );

  const validate = useCallback(
    (input: z.input<TSchema>) => {
      setHasSubmitted(true);
      return runZod(input);
    },
    [runZod],
  );

  const revalidate = useCallback(
    (input: z.input<TSchema>) => {
      if (!hasSubmitted) return;
      runZod(input);
    },
    [hasSubmitted, runZod],
  );

  const clearError = useCallback((key: TKey) => {
    setErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setOrderedKeys(prev => prev.filter(k => k !== key));
  }, []);

  const clearArrayError = useCallback((field: string, index: number, sub = "") => {
    setArrayErrors(prev => {
      const rows = prev[field];
      const row = rows?.[index];
      if (!row || !(sub in row)) return prev;
      const nextRow = { ...row };
      delete nextRow[sub];
      const nextRows = rows.slice();
      nextRows[index] = Object.keys(nextRow).length > 0 ? nextRow : undefined;
      // Drop the field entry entirely if no rows hold errors anymore.
      const hasAny = nextRows.some(r => r && Object.keys(r).length > 0);
      const next = { ...prev };
      if (hasAny) {
        next[field] = nextRows;
      } else {
        delete next[field];
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setErrors({});
    setArrayErrors({});
    setOrderedKeys([]);
    setOrphanMessages([]);
    setHasSubmitted(false);
  }, []);

  const setServerIssues = useCallback(
    (issues: ReadonlyArray<ZodIssueLike>) => {
      const {
        fieldErrors,
        arrayErrors: arrErrs,
        orphanMessages: orphans,
      } = mapZodIssuesToFieldErrors(issues, knownKeys, arrayKeys);
      const order: TKey[] = [];
      for (const issue of issues) {
        const head = issue.path.length > 0 ? String(issue.path[0]) : "";
        const full = issue.path.map(String).join(".");
        const matched =
          full && (knownKeys as ReadonlyArray<string>).includes(full)
            ? (full as TKey)
            : head && (knownKeys as ReadonlyArray<string>).includes(head)
              ? (head as TKey)
              : null;
        if (matched && !order.includes(matched)) order.push(matched);
      }
      setErrors(fieldErrors);
      setArrayErrors(arrErrs);
      setOrderedKeys(order);
      setOrphanMessages(orphans);
      setHasSubmitted(true);
    },
    [knownKeys, arrayKeys],
  );

  const firstInvalidKey = orderedKeys[0] ?? null;

  return {
    errors,
    arrayErrors,
    summary,
    orphanMessages,
    hasSubmitted,
    validate,
    revalidate,
    clearError,
    clearArrayError,
    reset,
    setServerIssues,
    firstInvalidKey,
  };
}
